import React, { useEffect, useState } from 'react';
import '../styles/AudioButton.css';

const AudioButton = ({ text, language }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [supported, setSupported] = useState(true);

  // Map our language codes to BCP 47 language tags for speech synthesis
  const languageMap = {
    'en': 'en-US',
    'ar': 'ar-SA', // Arabic (Saudi Arabia)
    'de': 'de-DE'  // German (Germany)
  };

  // Check if speech synthesis is supported and load voices when they become available
  useEffect(() => {
    if (!window.speechSynthesis) {
      setSupported(false);
      return;
    }
    
    const updateVoices = () => {
      const synth = window.speechSynthesis;
      const voices = synth.getVoices();
      setAvailableVoices(voices);
      console.log('Available voices:', voices.map(v => `${v.name} (${v.lang})`));
    };

    // Load voices immediately if they're available
    updateVoices();

    // Set up event listener for when voices are loaded
    window.speechSynthesis.onvoiceschanged = updateVoices;

    // Clean up event listener
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  const handleSpeak = () => {
    if (!supported) {
      setError('Text-to-speech is not supported in your browser.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const synth = window.speechSynthesis;
      const langTag = languageMap[language] || 'en-US';
      
      // Get fresh voices list
      const voices = availableVoices.length > 0 ? availableVoices : synth.getVoices();
      console.log('Available voices:', voices.map(v => `${v.name} (${v.lang})`));
      
      // Try to find the best matching voice
      let voice = voices.find(v => v.lang === langTag);
      
      // If no exact match, try to find a voice that starts with the language code
      if (!voice) {
        const langCode = langTag.split('-')[0];
        voice = voices.find(v => v.lang.startsWith(langCode));
      }
      
      // For Arabic, try to find any Arabic voice if specific one not found
      if (language === 'ar' && !voice) {
        voice = voices.find(v => v.lang.startsWith('ar'));
      }
      
      // If still no match, use any available voice (fallback)
      if (!voice && voices.length > 0) {
        voice = voices[0];
      }
      
      console.log(`Selected voice for ${language}:`, voice ? `${voice.name} (${voice.lang})` : 'No voice found');
      
      if (!voice) {
        throw new Error('No suitable voice found for the selected language.');
      }
      
      // Cancel any ongoing speech
      if (synth.speaking) {
        synth.cancel();
      }
      
      // Create a new utterance
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.voice = voice;
      utterance.lang = langTag;
      utterance.rate = language === 'ar' ? 0.8 : 0.9; // Slower for Arabic
      utterance.pitch = language === 'ar' ? 1.1 : 1; // Slight pitch adjustment for Arabic
      
      // Add event listeners
      utterance.onend = () => setIsLoading(false);
      utterance.onerror = (event) => {
        console.error('SpeechSynthesis error:', event);
        setError('Error occurred during speech. Please try again.');
        setIsLoading(false);
      };
      
      // Speak the text
      synth.speak(utterance);
      
    } catch (err) {
      console.error('Error with speech synthesis:', err);
      setError('Failed to read the text. Please check your browser\'s text-to-speech support.');
      setIsLoading(false);
    }
  };

  if (!supported) {
    return (
      <div className="audio-error">
        Text-to-speech is not supported in your browser.
      </div>
    );
  }

  return (
    <div className="audio-container">
      <button 
        className={`audio-button ${isLoading ? 'loading' : ''}`}
        onClick={handleSpeak}
        disabled={isLoading}
        title={`Pronounce in ${language === 'en' ? 'English' : language === 'ar' ? 'Arabic' : 'German'}`}
        aria-label={`Listen in ${language}`}
      >
        {isLoading ? '...' : <span role="img" aria-label="speaker">🔊</span>}
      </button>
      {error && <div className="audio-error">{error}</div>}
    </div>
  );
};

export default AudioButton;
